import { useEffect, useRef, useState } from 'preact/hooks';
import { loadMaps } from './lib/places';

export type RoofAerialProps = {
  lat: number;
  lng: number;
  /** The resolved address, used for the accessible name. Display only. */
  address: string;
};

/** Close enough to read a roof, far enough to see which building it is. */
const ZOOM = 20;

/**
 * A still aerial view of the roof the estimate was measured from.
 *
 * NOT `generator-sales-tool`'s `AerialPlacement`, and the task plan's instruction to
 * copy `web/src/lib/maps/` wholesale was not followed. That component is a draggable,
 * rotatable placer for a generator footprint: it needs `footprintScale`, an
 * `AdvancedMarkerElement`, a Map ID, and a Geocoder, none of which Studio has a use
 * for. It also ships its own copy of the Maps loader, which this repo already adapted
 * into `lib/places.ts` at Task 19, so copying the directory would have put two loaders
 * in one bundle racing to inject the same script tag.
 *
 * DELIBERATELY NON-INTERACTIVE. The widget is an iframe on the marketing site, and a
 * map that swallows the scroll wheel turns the page into a trap on the one screen the
 * customer has no reason to pan around. It is a picture of their roof, so it behaves
 * like one.
 *
 * FAILS OPEN. The numbers are the product here and the map is supporting, so a missing
 * key or a blocked script renders a caption rather than taking the result down. That is
 * also the state `npm run dev` runs in, since mock mode has no key.
 */
export function RoofAerial({ lat, lng, address }: RoofAerialProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await loadMaps();
        // Re-checked after every await. The customer can leave this screen while the
        // script is still in flight, and painting a map into a detached node throws
        // from inside Google's bundle where nothing useful catches it.
        if (cancelled || !hostRef.current) return;
        const { Map } = (await google.maps.importLibrary(
          'maps',
        )) as google.maps.MapsLibrary;
        if (cancelled || !hostRef.current) return;

        new Map(hostRef.current, {
          center: { lat, lng },
          zoom: ZOOM,
          mapTypeId: 'hybrid',
          tilt: 0,
          disableDefaultUI: true,
          // The three that actually make it inert. `disableDefaultUI` only removes the
          // controls; without these the map still pans, zooms on scroll, and takes
          // arrow keys away from the page.
          gestureHandling: 'none',
          keyboardShortcuts: false,
          clickableIcons: false,
        });
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lat, lng]);

  return (
    <figure class="aerial">
      {/*
        `role="img"` with a label, because a div Maps paints into has no implicit role
        and no accessible name. Without it the largest thing on the screen is simply
        absent to a screen reader. The label names the address rather than describing
        the imagery, since the address is the fact that matters.
      */}
      <div
        class="aerial-map"
        ref={hostRef}
        role="img"
        aria-label={`Aerial view of ${address}`}
      />
      {failed && (
        <figcaption class="hint">
          The aerial view is not available right now. It does not affect the figures
          above.
        </figcaption>
      )}
    </figure>
  );
}
